<template>
  <div class="industries-management">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-domain</v-icon>
                Industries Management
                <span class="page-inline-subtitle">Manage industry categories for AI agents</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  color="primary" 
                  @click="showCreateDialog = true"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Industry
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Industries Table -->
      <v-row>
        <v-col cols="12">
          <v-card class="industries-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      All Industries
                    </h2>
                    <div class="results-count" v-if="count > 0">
                      <div class="count-display">
                        <span class="count-number">{{ count }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Clean Filters Section -->
                  <div class="filters-section">
                    <v-text-field
                      v-model="form.search_keyword"
                      placeholder="Search industries..."
                      prepend-inner-icon="mdi-magnify"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-search clean-filter"
                      @click:clear="(form.search_keyword = null), (page = 1), getData()"
                      @keydown.enter="(page = 1), getData()"
                    ></v-text-field>
                    
                    <v-select
                      v-model="form.is_active"
                      :items="statuses"
                      placeholder="Status"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-select clean-filter"
                      @input="(page = 1), getData()"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small color="#64748b">mdi-check-circle-outline</v-icon>
                      </template>
                    </v-select>
                    
                    <div class="filter-actions">
                      <v-btn
                        @click="resetFilters()"
                        outlined
                        class="filter-reset-btn clean-btn"
                        small
                        color="#64748b"
                      >
                        <v-icon small left>mdi-refresh</v-icon>
                        Reset
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="industries"
                :loading="isLoading"
                :server-items-length="count"
                :items-per-page="rowsPerPage"
                :page="page"
                @update:page="updatePage"
                class="modern-data-table"
                hide-default-footer
              >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ (page - 1) * rowsPerPage + index + 1 }}</span>
              </template>

              <template v-slot:[`item.is_active`]="{ item }">
                <v-chip
                  :color="item.is_active ? 'green' : 'red'"
                  dark
                  small
                >
                  {{ item.is_active ? 'ACTIVE' : 'INACTIVE' }}
                </v-chip>
              </template>

              <template v-slot:[`item.industry_info`]="{ item }">
                <div class="industry-info">
                  <v-avatar size="40" class="industry-avatar mr-3">
                    <v-icon :color="item.color || '#667eea'">{{ item.icon || 'mdi-domain' }}</v-icon>
                  </v-avatar>
                  <div class="industry-details">
                    <div class="industry-name">{{ item.name || '' }}</div>
                    <div class="industry-description">{{ item.description || '' }}</div>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.agents_count`]="{ item }">
                <v-chip
                  color="blue"
                  outlined
                  small
                >
                  {{ item.agents_count || 0 }} Agents
                </v-chip>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  @click="editIndustry(item)"
                  color="primary"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="deleteIndustry(item)"
                  color="error"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              </v-data-table>
              
              <!-- Custom Pagination -->
              <div class="modern-pagination pa-4">
                <v-pagination
                  v-model="page"
                  :length="paginationLength"
                  :total-visible="7"
                  color="primary"
                  @input="getData"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Create/Edit Industry Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600px" persistent>
      <v-card class="industry-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">{{ editingIndustry ? 'Edit Industry' : 'Add New Industry' }}</h2>
              <p class="dialog-subtitle">{{ editingIndustry ? 'Update industry information' : 'Create a new industry category for AI agents' }}</p>
            </div>
            <v-btn 
              icon 
              @click="closeDialog"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="industryForm.name"
                outlined
                label="Industry Name *"
                prepend-inner-icon="mdi-domain"
                :error-messages="industryErrors.name"
                class="minimal-input"
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="industryForm.description"
                outlined
                label="Description"
                prepend-inner-icon="mdi-text"
                :error-messages="industryErrors.description"
                class="minimal-input"
                dense
                rows="3"
                hide-details="auto"
              ></v-textarea>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="industryForm.icon"
                :items="iconOptions"
                outlined
                label="Icon"
                prepend-inner-icon="mdi-emoticon-outline"
                class="minimal-input"
                dense
                hide-details="auto"
              >
                <template v-slot:selection="{ item }">
                  <v-icon class="mr-2">{{ item.value }}</v-icon>
                  {{ item.text }}
                </template>
                <template v-slot:item="{ item }">
                  <v-icon class="mr-2">{{ item.value }}</v-icon>
                  {{ item.text }}
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="industryForm.color"
                :items="colorOptions"
                outlined
                label="Color"
                prepend-inner-icon="mdi-palette"
                class="minimal-input"
                dense
                hide-details="auto"
              >
                <template v-slot:selection="{ item }">
                  <v-avatar size="20" :color="item.value" class="mr-2"></v-avatar>
                  {{ item.text }}
                </template>
                <template v-slot:item="{ item }">
                  <v-avatar size="20" :color="item.value" class="mr-2"></v-avatar>
                  {{ item.text }}
                </template>
              </v-select>
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="industryForm.is_active"
                inset
                color="primary"
                :label="`Status: ${industryForm.is_active ? 'Active' : 'Inactive'}`"
                class="minimal-switch"
                hide-details
              ></v-switch>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDialog"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveIndustry"
            :loading="isSaving"
            class="save-btn"
          >
            {{ editingIndustry ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "IndustriesPage",
  data() {
    return {
      form: {
        search_keyword: null,
        is_active: null,
      },
      industries: [],
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Status', value: 'is_active', sortable: true, width: '120px' },
        { text: 'Industry Info', value: 'industry_info', sortable: false, width: '350px' },
        { text: 'AI Agents', value: 'agents_count', sortable: false, width: '150px' },
        { text: 'Created', value: 'created_at', sortable: true, width: '150px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
      ],
      statuses: [
        { text: 'All', value: null },
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      page: 1,
      rowsPerPage: 15,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      showCreateDialog: false,
      editingIndustry: null,
      isSaving: false,
      industryForm: {
        name: '',
        description: '',
        icon: 'mdi-domain',
        color: '#667eea',
        is_active: true,
      },
      industryErrors: {},
      iconOptions: [
        { text: 'Domain', value: 'mdi-domain' },
        { text: 'Marketing', value: 'mdi-bullhorn' },
        { text: 'Finance', value: 'mdi-bank' },
        { text: 'Healthcare', value: 'mdi-hospital' },
        { text: 'Education', value: 'mdi-school' },
        { text: 'Technology', value: 'mdi-laptop' },
        { text: 'Retail', value: 'mdi-storefront' },
        { text: 'Manufacturing', value: 'mdi-factory' },
        { text: 'Transportation', value: 'mdi-truck' },
        { text: 'Real Estate', value: 'mdi-home' },
        { text: 'Entertainment', value: 'mdi-gamepad-variant' },
        { text: 'Food & Beverage', value: 'mdi-food' },
        { text: 'Agriculture', value: 'mdi-corn' },
      ],
      colorOptions: [
        { text: 'Blue', value: '#667eea' },
        { text: 'Green', value: '#10b981' },
        { text: 'Purple', value: '#8b5cf6' },
        { text: 'Red', value: '#ef4444' },
        { text: 'Orange', value: '#f97316' },
        { text: 'Pink', value: '#ec4899' },
        { text: 'Indigo', value: '#6366f1' },
        { text: 'Teal', value: '#14b8a6' },
      ],
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      this.form.search_keyword = this.form.search_keyword || "";
      this.form.is_active = this.form.is_active !== null ? this.form.is_active : "";
      
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&search_keyword=" +
        this.form.search_keyword +
        "&is_active=" +
        this.form.is_active;

      try {
        const response = await axios.get(`industries?${query}`);
        this.industries = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error('Error fetching industries:', error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
        // Fallback to sample industries data
        this.industries = [
          {
            id: 1,
            name: 'Marketing',
            description: 'Digital marketing, advertising, and content management solutions',
            icon: 'mdi-bullhorn',
            color: '#667eea',
            is_active: true,
            agents_count: 2,
            created_at: new Date().toISOString()
          },
          {
            id: 2,
            name: 'Technology',
            description: 'Software development, IT services, and technical solutions',
            icon: 'mdi-laptop',
            color: '#10b981',
            is_active: true,
            agents_count: 2,
            created_at: new Date().toISOString()
          },
          {
            id: 3,
            name: 'Finance',
            description: 'Financial services, banking, and investment solutions',
            icon: 'mdi-bank',
            color: '#8b5cf6',
            is_active: true,
            agents_count: 1,
            created_at: new Date().toISOString()
          },
          {
            id: 4,
            name: 'Healthcare',
            description: 'Medical services, patient care, and health management',
            icon: 'mdi-hospital',
            color: '#ef4444',
            is_active: true,
            agents_count: 1,
            created_at: new Date().toISOString()
          },
          {
            id: 5,
            name: 'Education',
            description: 'Educational services, learning platforms, and academic solutions',
            icon: 'mdi-school',
            color: '#f97316',
            is_active: true,
            agents_count: 1,
            created_at: new Date().toISOString()
          },
          {
            id: 6,
            name: 'Retail',
            description: 'E-commerce, retail management, and customer experience',
            icon: 'mdi-storefront',
            color: '#ec4899',
            is_active: true,
            agents_count: 1,
            created_at: new Date().toISOString()
          }
        ];
        this.count = this.industries.length;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      }
      this.isLoading = false;
    },
    updatePage(newPage) {
      this.page = newPage;
      this.getData();
    },
    resetFilters() {
      this.form.search_keyword = null;
      this.form.is_active = null;
      this.page = 1;
      this.getData();
    },
    editIndustry(industry) {
      this.editingIndustry = industry;
      this.industryForm = {
        name: industry.name,
        description: industry.description || '',
        icon: industry.icon || 'mdi-domain',
        color: industry.color || '#667eea',
        is_active: industry.is_active,
      };
      this.showCreateDialog = true;
    },
    closeDialog() {
      this.showCreateDialog = false;
      this.editingIndustry = null;
      this.industryForm = {
        name: '',
        description: '',
        icon: 'mdi-domain',
        color: '#667eea',
        is_active: true,
      };
      this.industryErrors = {};
    },
    async saveIndustry() {
      try {
        this.isSaving = true;
        this.industryErrors = {};
        
        if (this.editingIndustry) {
          await axios.put(`/industries/${this.editingIndustry.id}`, this.industryForm);
        } else {
          await axios.post('/industries', this.industryForm);
        }
        
        this.closeDialog();
        this.getData();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          this.industryErrors = error.response.data.errors;
        }
        console.error('Error saving industry:', error);
      }
      this.isSaving = false;
    },
    async deleteIndustry(industry) {
      if (confirm(`Are you sure you want to delete ${industry.name}? This action cannot be undone.`)) {
        try {
          await axios.delete(`/industries/${industry.id}`);
          this.getData();
        } catch (error) {
          console.error('Error deleting industry:', error);
        }
      }
    },
  },
};
</script>

<style scoped>
.industries-management {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

/* Industries Table Card */
.industries-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Filters Header Container */
.filters-header-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.count-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* Filters Section */
.filters-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

/* Clean Filter Styling */
.clean-filter {
  margin: 0 !important;
}

.clean-filter >>> .v-input__control {
  min-height: 40px !important;
}

.clean-filter >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
  padding: 0 16px !important;
  margin: 0 !important;
}

.clean-filter >>> .v-input__control .v-input__slot:before,
.clean-filter >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.clean-filter >>> .v-input__control .v-input__slot:hover {
  border-color: #9ca3af !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-filter >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #64748b !important;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.15) !important;
}

/* Remove any fieldset or additional borders */
.clean-filter >>> fieldset {
  display: none !important;
}

.clean-filter >>> .v-input__control:before,
.clean-filter >>> .v-input__control:after {
  display: none !important;
}

/* Search Filter - Wider */
.filter-search.clean-filter {
  width: 280px !important;
  min-width: 280px !important;
}

/* Select Filters */
.filter-select.clean-filter {
  width: 160px !important;
  min-width: 160px !important;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

/* Clean Buttons */
.clean-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  height: 40px !important;
  min-width: auto !important;
  transition: all 0.2s ease;
  font-size: 13px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

/* Reset Button */
.filter-reset-btn.clean-btn {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  color: #64748b !important;
}

.filter-reset-btn.clean-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}

/* Data Table */
.modern-data-table {
  background: transparent !important;
}

.modern-data-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-data-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-data-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-data-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-data-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-data-table >>> tbody tr td:last-child {
  border-right: none !important;
}

/* Serial Number Styling */
.serial-number {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

/* Industry Info Styling */
.industry-info {
  display: flex;
  align-items: center;
}

.industry-avatar {
  border: 2px solid rgba(102, 126, 234, 0.1);
  background: rgba(102, 126, 234, 0.1);
}

.industry-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.industry-description {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

/* Pagination */
.modern-pagination {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  justify-content: center;
}

.modern-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-weight: 500;
}

.modern-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* Action Buttons in Table */
.v-btn--icon {
  border-radius: 8px !important;
}

/* Chips in table */
.v-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Header Button Styling */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

.modern-btn:before {
  background-color: transparent;
}

/* Dialog Styling */
.industry-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  background: #f8f9fa;
}

/* Minimal Input Styling for Dialog */
.minimal-input {
  margin-bottom: 8px;
}

.minimal-input >>> .v-input__control {
  min-height: 56px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  padding: 0 16px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
  transform: translateY(-18px) scale(0.85) !important;
}

.minimal-input >>> input {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 15px;
  padding: 8px 0 !important;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 12px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Switch Styling */
.minimal-switch >>> .v-input--switch__thumb {
  color: #667eea !important;
}

.minimal-switch >>> .v-input--switch__track {
  color: #e2e8f0 !important;
}

.minimal-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #667eea !important;
}

.minimal-switch >>> .v-label {
  color: #2c3e50 !important;
  font-weight: 500;
  font-size: 15px;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

.save-btn {
  background: #667eea !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.save-btn:hover {
  background: #5a6fd8 !important;
}

.save-btn:before {
  background-color: transparent;
}

/* Responsive Design */
@media (max-width: 960px) {
  .filters-single-row {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .table-title-section {
    justify-content: center;
  }
  
  .filters-section {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
}

@media (max-width: 600px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-search.clean-filter,
  .filter-select.clean-filter {
    width: 100% !important;
    min-width: auto !important;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .clean-btn {
    width: 100%;
  }
  
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
}
</style>